import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import IconLogout from "@synthesis/shared-icons/IconLogout";
import IconUserSettings from "@synthesis/shared-icons/IconUserSettings";
import { IconList, IconNotification, IconOpenedBook, IconPieChart } from "@synthesis/shared-icons";
import { useGetProfile } from "@synthesis/shared-services/external/profile/hooks";
import { Counter, Popover, Tooltip, usePopover } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { useAuth } from "react-oidc-context";
import { useClientType } from "../../shared/hooks/use-client-type";
import { useUserInfo, type UserInfo } from "../../shared/hooks/use-user-info";
import { useUserRoles } from "../../shared/hooks/use-user-roles";
import { useNotificationsSidebar } from "../notifications-panel/hooks/use-notifications-sidebar";
import { useNotificationsUnreadCount } from "../notifications-panel/use-application-notifications";

const useDisplayUserInfo = (): UserInfo | null => {
  const userInfo = useUserInfo();
  const lastUserInfoRef = useRef(userInfo);

  if (userInfo) {
    lastUserInfoRef.current = userInfo;
  }

  return lastUserInfoRef.current;
};

const normalizeMetaPart = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";

  return trimmed && trimmed !== "—" ? trimmed : "";
};

const getUserMenuMetaLabel = (userInfo: UserInfo | null, profileOrganization?: string | null): string => {
  const roleLabel = normalizeMetaPart(userInfo?.roleLabel);
  const departmentLabel = normalizeMetaPart(userInfo?.departmentLabel);
  const organization = normalizeMetaPart(profileOrganization) || normalizeMetaPart(userInfo?.organization);

  if (roleLabel) {
    return departmentLabel ? `${roleLabel}, ${departmentLabel}` : roleLabel;
  }

  return organization;
};

const useUserMenuMetaLabel = (): string => {
  const userInfo = useDisplayUserInfo();
  const clientType = useClientType();
  const roleLabel = normalizeMetaPart(userInfo?.roleLabel);
  const { data: profile } = useGetProfile({
    query: { enabled: clientType === "external" && !roleLabel },
  });

  return getUserMenuMetaLabel(userInfo, profile?.organization);
};

const isElementTruncated = (element: HTMLElement) => element.scrollWidth > element.clientWidth;

const TruncatedTooltipText = ({ text, className }: { text: string; className?: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const element = textRef.current;

    if (!element) {
      return;
    }

    setIsTruncated(isElementTruncated(element));
  }, []);

  useLayoutEffect(() => {
    const element = textRef.current;

    if (!element) {
      return undefined;
    }

    checkTruncation();

    const frame = requestAnimationFrame(checkTruncation);
    const observer = new ResizeObserver(checkTruncation);

    observer.observe(element);

    const parent = element.parentElement;

    if (parent) {
      observer.observe(parent);
    }

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [text, checkTruncation]);

  return (
    <Tooltip description={text} disabled={!isTruncated} arrowSide="left" arrowAlign="start" delayDuration={500} noMaxWidth maxWidth="20rem">
      <span ref={textRef} className={cn("block w-full min-w-0 max-w-full truncate", className)}>
        {text}
      </span>
    </Tooltip>
  );
};

const UserMenuProfileHeader = ({ userInfo, metaLabel }: { userInfo: UserInfo | null; metaLabel: string }) => (
  <div className={cn("flex gap-3 overflow-visible p-3", metaLabel ? "items-start" : "items-center")}>
    <div className="bg-neutrals-90 flex size-10 shrink-0 items-center justify-center rounded-full">
      <span className="subtitle-sm text-type-minor">{userInfo?.initials ?? "—"}</span>
    </div>
    <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-visible">
      <TruncatedTooltipText text={userInfo?.fullName ?? "—"} className="subtitle-sm text-type-minor font-medium" />
      {metaLabel ? (
        <div className="bg-neutrals-90 w-fit rounded-lg px-2 py-1">
          <p className="subtitle-xs text-type-minor">{metaLabel}</p>
        </div>
      ) : null}
    </div>
  </div>
);

const navItemClass = "flex h-8 items-center gap-2 rounded-[10px] py-2 pl-3 pr-4 transition-colors hover:bg-neutrals-95/80";

const NavItem = ({ isActive, label, icon, to }: { isActive?: boolean; label: string; icon: ReactNode; to: string }) => (
  <Link to={to} className={cn(navItemClass, isActive && "bg-neutrals-99")} aria-current={isActive ? "page" : undefined}>
    <span className={cn("inline-flex size-5 shrink-0 items-center justify-center", isActive ? "text-brand-40" : "text-neutrals-60")}>{icon}</span>
    <span className={cn("subtitle-xs mt-0.5", isActive ? "text-brand-40" : "text-type-minor")}>{label}</span>
  </Link>
);

export const NavMenu = () => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { isInternalMonitoring, isInternalNtpChief, isInternalApparatusNg } = useUserRoles();
  const clientType = useClientType();
  const isApplications = pathname === "/" || pathname.startsWith("/applications/");
  const isMonitoring = pathname.startsWith("/monitoring");
  const isHistory = pathname.startsWith("/history");

  return (
    <nav className="flex items-center gap-1" aria-label="Основная навигация">
      {clientType === "internal" && (isInternalMonitoring || isInternalNtpChief || isInternalApparatusNg) ? (
        <NavItem isActive={isMonitoring} label="Мониторинг" to="/monitoring" icon={<IconPieChart className="size-5" aria-hidden />} />
      ) : null}
      <NavItem isActive={isApplications} label="Заявки" to="/" icon={<IconList className="size-5" aria-hidden />} />
      <NavItem isActive={isHistory} label="История" to="/history" icon={<IconOpenedBook className="size-5" aria-hidden />} />
    </nav>
  );
};

const UserMenuActionItem = ({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) => (
  <button
    type="button"
    className="hover:bg-neutrals-95/80 flex w-full items-center gap-4 rounded-[10px] p-3 text-left transition-colors"
    onClick={onClick}
  >
    <span className="text-neutrals-60 inline-flex size-6 shrink-0 items-center justify-center">{icon}</span>
    <span className="paragraph-base text-type-common">{label}</span>
  </button>
);

const UserMenuActions = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const clientType = useClientType();
  const { closePopover } = usePopover();
  const showProfileSettings = clientType === "external";

  const handleOpenProfileSettings = () => {
    closePopover();
    navigate({ to: "/user-info" });
  };

  return (
    <div className="flex flex-col p-2">
      {showProfileSettings ? (
        <UserMenuActionItem
          icon={<IconUserSettings className="size-6" aria-hidden />}
          label="Настройки профиля"
          onClick={handleOpenProfileSettings}
        />
      ) : null}
      <UserMenuActionItem icon={<IconLogout className="size-6" aria-hidden />} label="Выйти из аккаунта" onClick={() => auth.signoutRedirect()} />
    </div>
  );
};

export const AppHeaderUserBlock = () => {
  const userInfo = useDisplayUserInfo();
  const metaLabel = useUserMenuMetaLabel();
  const { open: openNotifications } = useNotificationsSidebar();
  const { count } = useNotificationsUnreadCount();

  return (
    <div className="flex items-center gap-4">
      <Popover
        align="end"
        side="bottom"
        isArrow={false}
        className="bg-neutrals-white z-50 min-w-[17.5rem] overflow-visible rounded-xl p-0 shadow outline-none"
        trigger={
          <button
            type="button"
            data-testid="eacd-user-menu-trigger"
            className="hover:bg-neutrals-95/80 flex w-[207px] items-center gap-2 rounded-[10px] p-1 transition-colors"
            aria-label="Меню пользователя"
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="bg-neutrals-90 flex size-10 shrink-0 items-center justify-center rounded-full">
                <span className="subtitle-sm text-type-minor">{userInfo?.initials ?? "—"}</span>
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="paragraph-sm text-type-minor truncate">{userInfo?.fullName ?? "—"}</p>
                {metaLabel ? <p className="subtitle-xs text-type-light truncate">{metaLabel}</p> : null}
              </div>
            </div>
          </button>
        }
      >
        <div className="flex max-w-[300px] flex-col overflow-visible">
          <UserMenuProfileHeader userInfo={userInfo} metaLabel={metaLabel} />

          <div className="bg-neutrals-80 h-px" aria-hidden />

          <UserMenuActions />
        </div>
      </Popover>

      <div className="bg-neutrals-90 h-10 w-px shrink-0" aria-hidden />

      <div className="relative shrink-0">
        <button
          type="button"
          className="hover:bg-neutrals-95/80 relative flex size-10 items-center justify-center rounded-xl p-2 transition-colors"
          aria-label="Уведомления"
          onClick={openNotifications}
        >
          <IconNotification className="text-neutrals-60 size-6" aria-hidden />
          {count > 0 ? (
            <span className="absolute -right-0.5 -top-1">
              <Counter count={count} />
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
};
